<script setup lang="ts">
import type { ComboBoxProps } from '@formwerk/core'
import { useComboBox, useDefaultFilter, useFormContext } from '@formwerk/core'
import { computed, useId } from 'vue'

const props = defineProps<ComboBoxProps<string>>()
const id = useId()
const triggerId = `--combobox-${id}`

const { contains } = useDefaultFilter({ caseSensitive: false })

// The input shows the selected option's label, so filtering by it would hide every other
// option: only filter once the user types something else.
let selectedLabel = (): string | undefined => undefined
const filter = Object.assign(
  (context: Parameters<typeof contains>[0]) => context.search === selectedLabel() || contains(context),
  { debounceMs: contains.debounceMs },
)

const {
  inputProps,
  labelProps,
  buttonProps,
  listBoxProps,
  errorMessageProps,
  errorMessage,
  isBlurred,
  inputValue,
  isListEmpty,
  selectedOption,
} = useComboBox(props, { filter })

selectedLabel = () => selectedOption.value?.label

const isDirty = computed(() => inputValue.value.length > 0)
// Inside a form, errors only show after a submit attempt, then update live as the user fixes them
const form = useFormContext()
const errored = computed(() => (form ? form.isSubmitAttempted.value : isBlurred.value) && !!errorMessage.value)
</script>

<template>
  <div>
    <div class="w-full relative" :class="{ 'c-red': errored }" :style="{ anchorName: triggerId }">
      <input
        v-bind="inputProps"
        placeholder=" "
        autocomplete="off"
        class="peer typo-sans-1 c-current leading-5 pb-0.75 pr-6 pt-3 appearance-none border-b border-current h-9 w-full block uppercase"
      >

      <label
        v-bind="labelProps"
        class="typo-sans-1 leading-5 motion-snug pointer-events-none select-none uppercase left-0 top-3 absolute peer-focus:scale-80 peer-focus:-translate-x-10% peer-focus:-translate-y-4"
        :class="{
          '-translate-y-4 scale-80 -translate-x-10%': isDirty,
        }"
      >
        {{ label }}
      </label>

      <div v-bind="buttonProps" class="pb-2 pt-3 flex cursor-pointer items-end bottom-0 right-0 top-0 absolute">
        <i class="i-icon-chevron-down size-4 icon" />
      </div>
    </div>

    <div
      v-bind="listBoxProps"
      popover
      class="c-white m-0 p-2 border border-current/15 bg-$primary-color max-h-40vh inset-auto overflow-y-auto"
      :style="{
        positionAnchor: triggerId,
        insetArea: 'bottom center',
        positionArea: 'bottom center',
        width: 'anchor-size(width)',
      }"
    >
      <slot />
      <p v-if="isListEmpty" class="typo-sans-1 px-2 py-0.5 opacity-50">
        {{ $t('no_results') }}
      </p>
    </div>

    <TransitionDefault>
      <p
        v-if="errored"
        v-bind="errorMessageProps"
        class="text-red-500 typo-sans-1 mt-1"
      >
        {{ errorMessage }}
      </p>
    </TransitionDefault>
  </div>
</template>

<style scoped>
[popover] {
  position-try-fallbacks:
    flip-block,
    flip-inline,
    flip-block flip-inline;
  position-try-order: max-height;
  transition:
    display 0.2s allow-discrete,
    opacity 0.2s allow-discrete,
    overlay 0.2s allow-discrete;
  opacity: 0;

  &:popover-open {
    opacity: 1;
  }
}

@starting-style {
  [popover]:popover-open {
    opacity: 0;
  }
}
</style>
